import { useState, type CSSProperties, type ReactNode } from "react";
import clsx from "clsx";
import type { TripSidebarController } from "../../hooks/useTripSidebarController";
import type { SortMode, TagFilterMode } from "../../hooks/usePoiFilters";
import { legPalette } from "../../theme/colors";
import { hasUsableCoordinates } from "../../utils/dayMapPois";
import { Button } from "../Button";
import {
  DisclosureSummary,
  EditorDisclosure,
  EditorSection,
} from "../EditorSection";
import { FormInput } from "../FormFields";
import { Modal } from "../Modal";
import { SegmentedControl } from "../SegmentedControl";

/** Tags shown before "Show all", so the dialog stays scannable. */
const COLLAPSED_TAG_COUNT = 16;

type MoreSection = "collections" | "sources" | "library" | "distance";

interface PoiFilterDialogProps {
  open: boolean;
  onClose: () => void;
  controller: TripSidebarController;
  boundsFilterEnabled: boolean;
  /** Only in the Map view: the bounds are the map on screen. */
  boundsFilterAvailable: boolean;
  onBoundsFilterChange: (enabled: boolean) => void;
  /** Clears every filter in the dialog. */
  onReset: () => void;
}

function poiCount(count: number): string {
  return `${count} ${count === 1 ? "POI" : "POIs"}`;
}

function modeLabel(mode: TagFilterMode | undefined): string {
  return mode === "include"
    ? ", included"
    : mode === "exclude"
      ? ", excluded"
      : "";
}

/**
 * Full-screen phone Filters dialog over the trip sidebar controller. Every
 * control applies immediately; the header and footer counts are the live
 * result pool, and each leg/tag option shows what it would leave.
 */
export function PoiFilterDialog({
  open,
  onClose,
  controller,
  boundsFilterEnabled,
  boundsFilterAvailable,
  onBoundsFilterChange,
  onReset,
}: PoiFilterDialogProps) {
  const {
    filteredPOIs,
    totalPoiCount,
    scheduledCounts,
    hideScheduled,
    setHideScheduled,
  } = controller;
  const resultCount = filteredPOIs.length;
  const share = totalPoiCount > 0 ? resultCount / totalPoiCount : 0;
  const plannedCount = scheduledCounts.size;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title="Filters"
      subtitle={
        <div className="mt-0.5">
          <p aria-live="polite" className="m-0 text-xs text-secondary">
            {resultCount} of {poiCount(totalPoiCount)}
          </p>
          <div
            role="progressbar"
            aria-label="POIs matching the filters"
            aria-valuemin={0}
            aria-valuemax={totalPoiCount}
            aria-valuenow={resultCount}
            className="mt-1 h-1 w-full max-w-40 overflow-hidden rounded-full bg-subtle"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              // Runtime geometry: the share of POIs left
              style={{ width: `${Math.round(share * 100)}%` }}
            />
          </div>
        </div>
      }
      headerActions={
        <Button
          variant="ghost"
          size="sm"
          className="min-h-11"
          onClick={onReset}
        >
          Reset
        </Button>
      }
      footer={
        <Button variant="primary" size="lg" fullWidth onClick={onClose}>
          {resultCount === 0
            ? "No POIs match"
            : `Show ${poiCount(resultCount)}`}
        </Button>
      }
    >
      {/* The header's own border already divides the first section */}
      <div className="flex flex-col gap-5 [&>section:first-child]:border-t-0 [&>section:first-child]:pt-0">
        <EditorSection id="filters-show" title="Show" icon="visibility">
          <SwitchRow
            label={`Hide planned (${plannedCount})`}
            checked={hideScheduled}
            onChange={setHideScheduled}
          />
          <SwitchRow
            label="Only in map area"
            hint={
              boundsFilterAvailable ? undefined : "Available in the Map view"
            }
            checked={boundsFilterAvailable && boundsFilterEnabled}
            disabled={!boundsFilterAvailable}
            onChange={onBoundsFilterChange}
          />
        </EditorSection>

        <SortSection controller={controller} />
        <LegSection controller={controller} />
        <TagSection controller={controller} />
        <MoreSections controller={controller} />
      </div>
    </Modal>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <Button
      variant="unstyled"
      aria-pressed={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-primary">
          {label}
        </span>
        {hint && <span className="block text-xs text-tertiary">{hint}</span>}
      </span>
      <span
        aria-hidden
        className={clsx(
          "relative h-6 w-10 shrink-0 rounded-full transition-colors",
          checked ? "bg-primary" : "bg-border",
        )}
      >
        <span
          className={clsx(
            "absolute top-0.5 left-0.5 size-5 rounded-full bg-surface shadow-sm transition-transform",
            checked && "translate-x-4",
          )}
        />
      </span>
    </Button>
  );
}

function SortSection({ controller }: { controller: TripSidebarController }) {
  const {
    sortMode,
    sortAscending,
    setSortMode,
    setSortAscending,
    targetPoi,
    setTargetPoiId,
  } = controller;
  const hasTarget = Boolean(targetPoi && hasUsableCoordinates(targetPoi));

  const changeSort = (mode: SortMode) => {
    // Distance follows the target: picking another order lets it go, or the
    // controller would switch straight back to distance.
    if (mode !== "distance" && controller.targetPoiId) setTargetPoiId(null);
    setSortMode(mode);
  };

  return (
    <EditorSection
      id="filters-sort"
      title="Sort by"
      icon="sort"
      actions={
        sortMode !== "distance" && (
          <Button
            variant="icon"
            size="lg"
            icon={sortAscending ? "arrow_upward" : "arrow_downward"}
            aria-label={sortAscending ? "Sort descending" : "Sort ascending"}
            onClick={() => setSortAscending(!sortAscending)}
          />
        )
      }
    >
      <SegmentedControl
        aria-label="Sort POIs"
        size="lg"
        fullWidth
        value={sortMode}
        onChange={changeSort}
        options={[
          { value: "alpha", label: "A–Z" },
          { value: "timeBudget", label: "Duration" },
          { value: "recentlyAdded", label: "Recent" },
          ...(hasTarget
            ? [{ value: "distance" as const, label: "Distance" }]
            : []),
        ]}
      />
      <p className="mt-2 mb-0 text-xs text-tertiary">
        {hasTarget && targetPoi ? (
          <span className="flex items-center gap-2">
            <span className="min-w-0 truncate">
              Distance from {targetPoi.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 shrink-0"
              onClick={() => setTargetPoiId(null)}
            >
              Clear target
            </Button>
          </span>
        ) : (
          "Set a target on a result to sort by distance."
        )}
      </p>
    </EditorSection>
  );
}

/** A leg or tag option; its accessible name reads "Food, 3 POIs, included". */
function FacetChip({
  name,
  label,
  count,
  mode,
  onToggle,
  leading,
  activeStyle,
  activeClassName = "bg-primary text-white border-primary",
}: {
  name: string;
  label: ReactNode;
  count: number;
  mode: TagFilterMode | undefined;
  onToggle: () => void;
  leading?: ReactNode;
  activeStyle?: CSSProperties;
  activeClassName?: string;
}) {
  return (
    <Button
      variant="unstyled"
      aria-pressed={mode === "include"}
      aria-label={`${name}, ${poiCount(count)}${modeLabel(mode)}`}
      onClick={onToggle}
      className={clsx(
        "inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors",
        mode === "include"
          ? activeClassName
          : mode === "exclude"
            ? "border-error bg-error-bg text-error"
            : "border-border bg-surface text-primary",
        !mode && count === 0 && "opacity-50",
      )}
      style={mode === "include" ? activeStyle : undefined}
    >
      {mode === "exclude" ? (
        <span className="material-symbols-outlined text-base" aria-hidden>
          block
        </span>
      ) : (
        leading
      )}
      <span className={clsx(mode === "exclude" && "line-through")}>
        {label}
      </span>
      <span
        className={clsx(
          "tabular-nums text-xs font-semibold",
          mode === "include" ? "opacity-75" : "text-tertiary",
        )}
      >
        {count}
      </span>
    </Button>
  );
}

function LegSection({ controller }: { controller: TripSidebarController }) {
  const { legOptions, legFilters, legFacetCounts, toggleLeg } = controller;
  if (legOptions.length === 0) return null;
  return (
    <EditorSection id="filters-legs" title="Legs" icon="route">
      <div className="flex flex-wrap gap-2">
        {legOptions.map((leg) => {
          const mode = legFilters.get(leg.id);
          const count = legFacetCounts.get(leg.id) ?? 0;
          return (
            <FacetChip
              key={leg.id}
              name={leg.name}
              label={leg.name}
              count={count}
              mode={mode}
              onToggle={() => toggleLeg(leg.id)}
              activeClassName={clsx(
                "border-transparent text-white",
                leg.color === undefined && "bg-neutral",
              )}
              // Runtime palette values: each leg carries its own colour
              activeStyle={
                leg.color === undefined
                  ? undefined
                  : { backgroundColor: legPalette(leg.color).dot }
              }
              leading={
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{
                    backgroundColor:
                      mode === "include"
                        ? "currentColor"
                        : leg.color === undefined
                          ? "var(--color-neutral)"
                          : legPalette(leg.color).dot,
                  }}
                />
              }
            />
          );
        })}
      </div>
    </EditorSection>
  );
}

function TagSection({ controller }: { controller: TripSidebarController }) {
  const { tagFacets, tagMatchMode, setTagMatchMode, toggleTag } = controller;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? tagFacets : tagFacets.slice(0, COLLAPSED_TAG_COUNT);

  return (
    <EditorSection
      id="filters-tags"
      title="Tags"
      icon="sell"
      actions={
        <SegmentedControl
          aria-label="Tag matching mode"
          size="lg"
          value={tagMatchMode}
          onChange={setTagMatchMode}
          options={[
            { value: "all", label: "All", ariaLabel: "Match all tags" },
            { value: "any", label: "Any", ariaLabel: "Match any tag" },
          ]}
        />
      }
    >
      {tagFacets.length === 0 ? (
        <p className="m-0 text-xs text-tertiary">No tags in this trip yet.</p>
      ) : (
        <>
          <p className="mt-0 mb-2 text-xs text-tertiary">
            Tap once to include, twice to exclude, three times to clear.
          </p>
          <div className="flex flex-wrap gap-2">
            {visible.map((facet) => (
              <FacetChip
                key={facet.tag}
                name={`${facet.label}${facet.ownerName ? ` (${facet.ownerName})` : ""}`}
                count={facet.count}
                mode={facet.mode}
                onToggle={() => toggleTag(facet.tag)}
                label={
                  <>
                    {facet.kind === "category" && (
                      <span aria-hidden className="mr-1 font-bold">
                        +
                      </span>
                    )}
                    {facet.label}
                    {facet.ownerName && (
                      <span className="ml-1 text-2xs opacity-70">
                        {facet.ownerName}
                      </span>
                    )}
                  </>
                }
              />
            ))}
          </div>
          {tagFacets.length > COLLAPSED_TAG_COUNT && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 min-h-11"
              aria-expanded={showAll}
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? "Show fewer tags"
                : `Show all ${tagFacets.length} tags`}
            </Button>
          )}
        </>
      )}
    </EditorSection>
  );
}

function MoreSections({ controller }: { controller: TripSidebarController }) {
  const [openSection, setOpenSection] = useState<MoreSection | null>(null);
  const toggle = (section: MoreSection) => (open: boolean) =>
    setOpenSection(open ? section : null);
  const {
    collectionOptions,
    collectionFilters,
    toggleCollection,
    allSourceTexts,
    sourceFilters,
    toggleSource,
    ownerOptions,
    ownerFilters,
    toggleOwner,
    targetPoi,
    maximumDistanceKm,
    setMaximumDistanceKm,
  } = controller;
  const hasTarget = Boolean(targetPoi && hasUsableCoordinates(targetPoi));
  const showLibrary = ownerOptions.some((owner) => !owner.isSelf);

  return (
    <section aria-labelledby="filters-more" className="flex flex-col">
      <h3 id="filters-more" className="m-0 mb-1 text-sm font-bold text-primary">
        More
      </h3>
      {collectionOptions.length > 0 && (
        <EditorDisclosure
          id="filters-collections"
          title="Collections"
          icon="collections_bookmark"
          touch
          open={openSection === "collections"}
          onOpenChange={toggle("collections")}
          emptyLabel="Any"
          summary={
            collectionFilters.size > 0 ? (
              <DisclosureSummary
                text="Filtering"
                count={collectionFilters.size}
              />
            ) : null
          }
        >
          <OptionChips
            options={collectionOptions.map((c) => ({
              id: c.id,
              label: c.name,
            }))}
            filters={collectionFilters}
            onToggle={toggleCollection}
          />
        </EditorDisclosure>
      )}
      {allSourceTexts.length > 0 && (
        <EditorDisclosure
          id="filters-sources"
          title="Sources"
          icon="bookmark"
          touch
          open={openSection === "sources"}
          onOpenChange={toggle("sources")}
          emptyLabel="Any"
          summary={
            sourceFilters.size > 0 ? (
              <DisclosureSummary text="Filtering" count={sourceFilters.size} />
            ) : null
          }
        >
          <OptionChips
            options={allSourceTexts.map((source) => ({
              id: source,
              label: source,
            }))}
            filters={sourceFilters}
            onToggle={toggleSource}
          />
        </EditorDisclosure>
      )}
      {showLibrary && (
        <EditorDisclosure
          id="filters-library"
          title="Library"
          icon="group"
          touch
          open={openSection === "library"}
          onOpenChange={toggle("library")}
          emptyLabel="Any"
          summary={
            ownerFilters.size > 0 ? (
              <DisclosureSummary text="Filtering" count={ownerFilters.size} />
            ) : null
          }
        >
          <OptionChips
            options={ownerOptions.map((o) => ({ id: o.id, label: o.name }))}
            filters={ownerFilters}
            onToggle={toggleOwner}
          />
        </EditorDisclosure>
      )}
      <EditorDisclosure
        id="filters-distance"
        title="Distance from target"
        icon="gps_fixed"
        touch
        open={openSection === "distance"}
        onOpenChange={toggle("distance")}
        emptyLabel="Any"
        summary={
          maximumDistanceKm !== null ? (
            <DisclosureSummary text={`Within ${maximumDistanceKm} km`} />
          ) : null
        }
      >
        {hasTarget && targetPoi ? (
          <DistanceField
            key={targetPoi.id}
            targetName={targetPoi.name}
            value={maximumDistanceKm}
            onChange={setMaximumDistanceKm}
          />
        ) : (
          <p className="m-0 text-xs text-tertiary">
            Set a target on a result first.
          </p>
        )}
      </EditorDisclosure>
    </section>
  );
}

function OptionChips({
  options,
  filters,
  onToggle,
}: {
  options: { id: string; label: string }[];
  filters: Map<string, TagFilterMode>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const mode = filters.get(option.id);
        return (
          <Button
            key={option.id}
            variant="unstyled"
            aria-pressed={mode === "include"}
            aria-label={`${option.label}${modeLabel(mode)}`}
            onClick={() => onToggle(option.id)}
            className={clsx(
              "inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors",
              mode === "include"
                ? "border-primary bg-primary text-white"
                : mode === "exclude"
                  ? "border-error bg-error-bg text-error line-through"
                  : "border-border bg-surface text-primary",
            )}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

function DistanceField({
  targetName,
  value,
  onChange,
}: {
  targetName: string;
  value: number | null;
  onChange: (distanceKm: number | null) => void;
}) {
  const [draft, setDraft] = useState(value?.toString() ?? "");
  const parsed = Number(draft);
  const invalid = draft !== "" && (!Number.isFinite(parsed) || parsed <= 0);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="form-label m-0" htmlFor="filters-distance-input">
        Within (km) of {targetName}
      </label>
      <FormInput
        id="filters-distance-input"
        type="number"
        inputMode="decimal"
        min="0.1"
        step="0.1"
        placeholder="Any distance"
        value={draft}
        onChange={(next) => {
          setDraft(next);
          const distance = Number(next);
          onChange(
            next !== "" && Number.isFinite(distance) && distance > 0
              ? distance
              : null,
          );
        }}
      />
      {invalid && (
        <p role="alert" className="m-0 text-xs text-error">
          Enter a distance greater than 0 km
        </p>
      )}
    </div>
  );
}
